import _Forum from "@mui/icons-material/Forum";
import _Typography from "@mui/material/Typography";
import _OpenInFull from "@mui/icons-material/OpenInFull";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _ThumbUp from "@mui/icons-material/ThumbUp";
import _Button from "@mui/material/Button";
import _ThumbDown from "@mui/icons-material/ThumbDown";
import { ThemeProvider } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { PhotosDialog, RemoveIcon } from '../../components';
import { Ratings, reportTypesTitleKeys } from '../../models';
export function ReportDate({
  report: {
    created,
    creator,
    startDate,
    endDate
  },
  source
}) {
  const from = startDate && startDate.format('LL') || null;
  const to = endDate && endDate.format('LL') || null;
  if (from && to) return /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.periods.from_to",
    values: {
      from,
      to
    }
  });
  if (from) return /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.periods.from",
    values: {
      from
    }
  });
  if (to) return /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.periods.until",
    values: {
      to
    }
  });
  if (!source && creator) return /*#__PURE__*/React.createElement(Trans, {
    components: [/*#__PURE__*/React.createElement("b", {
      key: 0
    })],
    i18nKey: "commons.created_by_on",
    values: {
      creator,
      created: created.format('LL')
    }
  });
  return /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.created_on",
    values: {
      created: created.format('LL')
    }
  });
}
export function ReportReviews({
  currentUser,
  report,
  onRating
}) {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(false);
  }, [report]);
  function handleRating(rating) {
    const ownReview = reviews?.find(({
      creator,
      rating
    }) => creator?.id === currentUser?.id && rating) || null;
    setLoading(true);
    onRating(report, ownReview, rating);
  }
  const {
    reviews
  } = report;
  const ownReview = reviews?.find(({
    creator,
    rating
  }) => creator?.id === currentUser?.id && rating) || null;
  const nbUsefulReviews = reviews?.filter(({
    rating
  }) => rating === Ratings.Useful).length || 0;
  const nbNegativeReviews = reviews?.filter(({
    rating
  }) => rating === Ratings.NotUseful).length || 0;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Button, {
    color: ownReview?.rating === Ratings.NotUseful ? 'error' : 'inherit',
    disabled: loading,
    onClick: () => handleRating(Ratings.NotUseful),
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(_ThumbDown, null),
    variant: "outlined"
  }, "(", nbNegativeReviews, ")"), /*#__PURE__*/React.createElement(_Button, {
    color: ownReview?.rating === Ratings.Useful ? 'success' : 'inherit',
    disabled: loading,
    onClick: () => handleRating(Ratings.Useful),
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(_ThumbUp, null),
    variant: "outlined"
  }, "(", nbUsefulReviews, ")"));
}
export function ReportPopup({
  currentUser,
  report,
  theme,
  sourcesMap,
  onClose,
  onDetailsClick,
  onRemove,
  openCommentsDialog,
  onRating
}) {
  const [showedPhotos, showPhotos] = useState(null);
  const [photosDialogOpen, openPhotosDialog] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    typeCode,
    photos,
    sourceId,
    description,
    reviews
  } = report;
  const titleKey = reportTypesTitleKeys[typeCode];
  const source = sourceId !== null && sourcesMap?.[sourceId] || null;
  const nbComments = reviews?.filter(({
    comment,
    photos
  }) => comment || photos.length > 0).length || 0;
  const canRemove = Boolean(onRemove && currentUser && report.creatorId && report.creatorId === currentUser.id);
  function handlePhotosDialogOpen() {
    if (report.photos.length === 0) return;
    showPhotos(report.photos.map(({
      url
    }, index) => ({
      title: `${t(titleKey || 'commons.report.default_title')} ${report.id}${report.photos.length > 1 ? ` #${index + 1}` : ``}`,
      url
    })));
    openPhotosDialog(true);
  }
  function handlePhotosDialogClose() {
    openPhotosDialog(false);
    setTimeout(() => showPhotos(null), theme.transitions.duration.leavingScreen);
  }
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(CloseButtonWrapper, null, /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "left",
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.actions.close"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    onClick: () => onClose(),
    size: "small"
  }, /*#__PURE__*/React.createElement(_Close, null)))), /*#__PURE__*/React.createElement(StyledHeader, null, /*#__PURE__*/React.createElement("div", {
    style: {
      overflowX: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(StyledTitle, {
    variant: "h6"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: titleKey || 'commons.report.default_title'
  }), /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0#", report.id)))), photos[0] && /*#__PURE__*/React.createElement(StyledPhoto, {
    image: photos[0].url
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "left",
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.actions.show_photo"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    onClick: handlePhotosDialogOpen,
    size: "small"
  }, /*#__PURE__*/React.createElement(_OpenInFull, {
    fontSize: "small"
  }))))), /*#__PURE__*/React.createElement(StyledContent, {
    style: {
      paddingTop: photos[0] ? 16 : 0
    }
  }, source && /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    component: "p",
    variant: "caption"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.report.source",
    values: {
      source: source.title
    }
  })), /*#__PURE__*/React.createElement(_Typography, {
    gutterBottom: true,
    color: "textSecondary",
    component: "p",
    variant: "caption"
  }, /*#__PURE__*/React.createElement(ReportDate, {
    report: report,
    source: source
  })), description && /*#__PURE__*/React.createElement(_Typography, {
    variant: "body2"
  }, description)), (onDetailsClick || openCommentsDialog || onRating || canRemove) && /*#__PURE__*/React.createElement(StyledActions, null, canRemove && /*#__PURE__*/React.createElement(_Tooltip, {
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.actions.remove"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    className: "remove-button",
    onClick: () => onRemove?.(report),
    size: "small"
  }, /*#__PURE__*/React.createElement(RemoveIcon, {
    color: "error",
    fontSize: "small"
  }))), onDetailsClick && /*#__PURE__*/React.createElement(_Button, {
    color: "primary",
    onClick: onDetailsClick,
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.more_details"
  })), openCommentsDialog && /*#__PURE__*/React.createElement(_Button, {
    color: "inherit",
    onClick: () => openCommentsDialog(true),
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(_Forum, null),
    variant: "outlined"
  }, "(", nbComments, ")"), onRating && /*#__PURE__*/React.createElement(ReportReviews, {
    currentUser: currentUser,
    onRating: onRating,
    report: report
  }))), /*#__PURE__*/React.createElement(PhotosDialog, {
    onClose: handlePhotosDialogClose,
    open: photosDialogOpen,
    photos: showedPhotos
  }));
}
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: max(calc(50vh - 24px), 400px);
  overflow-y: hidden;
  position: relative;
  width: min(100vw - 120px, 350px);
`;
const CloseButtonWrapper = styled.div`
  padding: 8px;
  position: absolute;
  right: 0;
  top: 0;

  button {
    background-color: rgba(255, 255, 255, 0.9);

    &:hover {
      background-color: rgba(255, 255, 255, 0.8);
    }
  }
`;
const StyledHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  padding: 16px 16px 8px 16px;

  &.shifted {
    padding: 16px 54px 8px 16px;
  }
`;
const StyledTitle = styled(_Typography)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const StyledPhoto = styled.div`
  background-image: ${({
  image
}) => `url(${image})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: 50%;
  position: relative;

  div {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    position: absolute;
    right: 8px;
    top: 8px;
  }
`;
const StyledContent = styled.div`
  flex-shrink: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
`;
const StyledActions = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  height: 48px;
  padding: 0 16px;

  button {
    margin-left: 8px;
  }

  .remove-button {
    margin-left: 0;
    margin-right: auto;
  }
`;